package com.htmlagent.site.state.chat

import com.htmlagent.shared.stream.TurnStage

/** A turn of the conversation, as it is painted. */
data class ChatMessage(
    val author: Author,
    val text: String,
    /** While the agent writes: the last message grows token by token. */
    val streaming: Boolean = false,
    /** How long the turn took, in seconds: a measured wait feels shorter. */
    val seconds: Int? = null,
    /** If the turn ended in a page, its summary. */
    val artifact: ArtifactCard? = null,
    /**
     * The turn ended without producing a page, and it was not cancelled.
     *
     * Without it, a turn that produced nothing is painted exactly like one that produced a page:
     * the only difference on screen was a missing copy button, which nobody reads as a failure.
     */
    val noPage: Boolean = false,
    /**
     * Written by the product and not by the model, and the only message allowed to carry a link.
     *
     * The chat paints Markdown to nodes and drops links on purpose, because the text came from a
     * model. This one did not, so it says so in the type rather than trusting the renderer.
     */
    val tip: Boolean = false,
    /**
     * The server's identifier, which editing and deleting are addressed by. Null until the turn
     * is saved, and for good on what the server never keeps: failures and product notices.
     */
    val id: Long? = null,
    /**
     * Rebuilt from the saved history rather than arriving now. Only what arrives is animated:
     * reopening a session would otherwise set the whole thread moving at once.
     */
    val restored: Boolean = false,
    /**
     * Where the reference photos sent with this request are painted from: `data:` URLs for one
     * just sent, the server's for a saved one. Empty for everything else.
     */
    val images: List<String> = emptyList(),
) {
    enum class Author { USER, AGENT }
}

/**
 * A turn's receipt: what came out of it. Everything comes from real HTML data (sections, size,
 * version); nothing is made up to fill the card.
 */
data class ArtifactCard(
    val version: Int,
    /** What the person reads: `3`, or `3.1` for a fix on it. */
    val label: String,
    val sections: List<String>,
    val sizeInBytes: Int,
)

/**
 * What the agent is doing right now. A turn lasts a long minute and a generic indicator is not
 * enough: reading instructions and writing the page are different waits.
 */
sealed interface Activity {
    data object Idle : Activity
    data object Thinking : Activity
    /**
     * [subject] is the skill or the section, when the backend sends it. It is never raw text from
     * the model: it only arrives if it passed domain validation, so it is painted as it is.
     */
    data class UsingTool(val tool: String, val subject: String? = null) : Activity
    data object WritingPage : Activity

    /**
     * One of the waits the backend names — planning, checking, reviewing, correcting, saving —
     * for as long as it lasts. A tool in the middle shows itself and then hands back to it.
     */
    data class InStage(val stage: TurnStage) : Activity

    /**
     * A stop was requested and it has not stopped yet.
     *
     * Cancelling is not immediate: the turn's coroutine does not notice until a suspension point,
     * which with the provider thinking can take seconds. Without this state, pressing "parar"
     * changed nothing on screen.
     */
    data object Stopping : Activity
}

/**
 * Which of the three screens we are on, as a closed hierarchy and not as loose booleans: with a
 * stream that can reconnect mid-turn, impossible states show up on their own.
 */
sealed interface ChatUiState {

    /** Before the first session: there is nothing to show yet. */
    data object Connecting : ChatUiState

    /**
     * Session open. It carries no data on purpose: each panel collects its own flow from the
     * presenter, so a text token does not recompose the whole tree.
     */
    data object Ready : ChatUiState

    /** It could not even start. The text comes already written: the UI does not translate codes. */
    data class Failed(val message: String) : ChatUiState
}

/**
 * State of the artifact panel.
 *
 * `Writing` and `Ready` are separate on purpose: a half-written page looks broken and the user
 * concludes the agent failed. While it arrives, the skeleton of closed sections is shown, which
 * is real progress taken from the HTML markers.
 */
sealed interface ArtifactState {

    /** Nothing has been generated in this session yet. */
    data object None : ArtifactState

    /** Arriving. The page's shape is shown, never its code nor its half-done render. */
    data class Writing(
        val title: String?,
        val sections: List<SectionProgress>,
    ) : ArtifactState

    /** Finished and saved: now it can be rendered. */
    data class Ready(
        val version: Int,
        /** What the person reads: `3`, or `3.1` for a fix on it. */
        val label: String,
        val url: String,
        /** The whole HTML, for the code view and for the copy button. */
        val html: String,
    ) : ArtifactState

    /** Discarded because it came in truncated. The message explains what to do. */
    data class Discarded(val message: String) : ArtifactState
}
